<!-- Ordre : trous puis terrain, import avant export (l'import des trous est dans la barre d'outils de l'onglet Holes) -->
<div class="viewer-toolbar">
  <app-button
    variant="primary"
    color="amarante"
    size="small"
    [label]="'GLOBAL.EXPORT_HOLES' | translate"
    [addIconLeft]="true"
    (clicked)="openExportModal()"
    data-attr="blast-3d-btn-export-holes">
    <i slot="icon-left" class="fas fa-download"></i>
  </app-button>
  <app-button
    variant="secondary"
    color="amarante"
    size="small"
    [label]="'GLOBAL.IMPORT_TERRAIN' | translate"
    [addIconLeft]="true"
    (clicked)="triggerFileInput()"
    data-attr="blast-3d-btn-import-terrain">
    <i slot="icon-left" class="fas fa-file-import"></i>
  </app-button>
  <app-button
    variant="tertiary"
    color="amarante"
    size="small"
    [label]="isSavingTerrain() ? ('GLOBAL.SAVING' | translate) + '...' : ('GLOBAL.SAVE_TERRAIN' | translate)"
    [addIconLeft]="true"
    [state]="!hasLoadedObjModels() || isSavingTerrain() ? 'disabled' : 'default'"
    (clicked)="saveTerrain()"
    data-attr="blast-3d-btn-save-terrain">
    <i slot="icon-left" class="fas fa-save"></i>
  </app-button>
</div>
<input #fileInput type="file" (change)="onFileSelected($event)" (click)="$event.stopPropagation()" multiple accept=".obj,.mtl,.jpg,.jpeg,.png" style="display: none;" />
<div class="canvas-container">
  <!-- data-terrain-loading : fin du chargement des terrains, attendue par les tests E2E -->
  <div class="canvas-wrapper" [attr.data-terrain-loading]="isLoadingTerrain()">
    <canvas #canvas3d id="canvas3d" (dragover)="onDragOver($event)" (dragleave)="onDragLeave($event)"
    (drop)="onDrop($event)"></canvas>

    <!-- Terrains du tir : plusieurs versions possibles (avant / après forage) -->
    @if (terrainLayers().length > 1) {
      <div class="terrain-layers-panel" data-attr="terrain-layers">
        <!-- track $index : deux OBJ sans nom donnent deux entrées homonymes, et un track
             par nom lèverait NG0955. Le rang est de toute façon l'identifiant partout. -->
        @for (layer of terrainLayers(); track $index; let i = $index) {
          <button type="button" class="terrain-layer" [class.terrain-layer--hidden]="!layer.visible"
                  [attr.data-attr]="'terrain-layer-' + i"
                  [attr.data-visible]="layer.visible" [attr.aria-pressed]="layer.visible"
                  (click)="toggleTerrainLayer(i)" [title]="layer.name">
            <i class="fas" [class.fa-eye]="layer.visible" [class.fa-eye-slash]="!layer.visible" aria-hidden="true"></i>
            <span class="terrain-layer__name">{{ layer.name }}</span>
          </button>
        }
      </div>
    }

    <!-- Colonne de droite : les bulles d'info en haut, le curseur géologique centré dans la place restante,
         raccourci si la vue est basse : ils ne se recouvrent jamais. -->
    <div class="viewer-side">
      <!-- Charge Info Panel -->
      @if (selectedChargeInfo(); as charge) {
        <div class="charge-info-panel">
          <h4>{{ 'VIEWER_3D.CHARGE_INFO' | translate }}</h4>
          <div><b>{{ 'GLOBAL.PRODUCT' | translate }}:</b> {{charge.productName || charge.productId}}</div>
          @if (charge.holeName) {
            <div><b>{{ 'VIEWER_3D.HOLE_NAME' | translate }}:</b> {{charge.holeName}}</div>
          }
          <div><b>{{ 'VIEWER_3D.LENGTH' | translate }}:</b> {{getChargeLength(charge) | number:'1.2-2'}} m</div>
          <div><b>{{ 'VIEWER_3D.QUANTITY' | translate }}:</b> {{ charge.isBulk ? (charge.quantity | number:'1.1-1') + ' kg' : charge.quantity }}</div>
        </div>
      }

      <!-- Hole Info Panel : tête de trou, disponible quel que soit l'état du trou -->
      @if (selectedHoleInfo(); as hole) {
        <div class="charge-info-panel">
          <h4>{{ 'VIEWER_3D.HOLE_INFO' | translate }}</h4>
          <div><b>{{ 'VIEWER_3D.HOLE_NAME' | translate }}:</b> {{hole.name}}</div>
          @if (hole.Geometry?.length) {
            <div><b>{{ 'VIEWER_3D.LENGTH' | translate }}:</b> {{hole.Geometry.length | number:'1.2-2'}} m</div>
          }
        </div>
      }

      <!-- Modèle géologique (vue Géologie) : curseur vertical, il construit le modèle du fond vers la surface par pas de 10 % -->
      @if (geologyKind(); as kind) {
        <div class="geology-depth" data-attr="blast-3d-geology-depth">
          <label class="geology-depth__title" for="geology-depth-range">
            <i class="fas fa-layer-group" aria-hidden="true"></i>
            <span class="geology-depth__title-text">{{ (kind === 'lithology' ? 'HOLES_TAB.GEOLOGY_MODE_LITHOLOGY' : 'HOLES_TAB.GEOLOGY_MODE_DRILL_LOG') | translate }}</span>
          </label>
          <span class="geology-depth__value">{{ geologyPercent() }} %</span>

          <span class="geology-depth__end">{{ 'HOLES_TAB.GEOLOGY_3D_TOP' | translate }}</span>
          <div class="geology-depth__track">
            <input
              #geologyDepthRange
              id="geology-depth-range"
              class="geology-depth__range"
              type="range"
              aria-orientation="vertical"
              min="0"
              max="100"
              step="10"
              [style.--fill]="geologyPercent() + '%'"
              [value]="geologyPercent()"
              [attr.aria-label]="(kind === 'lithology' ? 'HOLES_TAB.GEOLOGY_MODE_LITHOLOGY' : 'HOLES_TAB.GEOLOGY_MODE_DRILL_LOG') | translate"
              [attr.aria-valuetext]="'HOLES_TAB.GEOLOGY_3D_DEPTH' | translate: { percent: geologyPercent() }"
              (input)="onGeologyPercentChange(geologyDepthRange.value)" />
            <div class="geology-depth__ticks" aria-hidden="true">
              @for (step of [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100]; track step) {
                <span class="geology-depth__tick" [class.geology-depth__tick--on]="step <= geologyPercent()"></span>
              }
            </div>
          </div>
          <span class="geology-depth__end">{{ 'HOLES_TAB.GEOLOGY_3D_BOTTOM' | translate }}</span>
        </div>
      }
    </div>
  </div>
</div>

